import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { formsAPI, cvAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Common/Modal';
import { PlusIcon, TrashIcon, PencilIcon, Bars3Icon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio (single choice)' },
  { value: 'checkbox', label: 'Checkboxes (multi-choice)' },
];

/** Types that carry a list of options. */
const OPTION_TYPES = ['select', 'radio', 'checkbox'];

/**
 * Types that can carry an answer key. RADIO ONLY, and the server decides this,
 * not this file: routers/forms.py::_validate_correct_option rejects a key on
 * anything else with 400 "Only radio questions can have a correct option", on
 * create and on update alike. Widening this list without widening that check
 * ships a field whose every save fails.
 *
 * Why the server draws it there:
 *   - checkbox takes a LIST of answers, and form_screening_service.py
 *     ::_answer_to_text flattens a list with ", " before comparing it to
 *     correct_option as one string. A key chosen from a single-select could
 *     only ever match someone who ticked exactly one box; everyone else grades
 *     wrong, and the portal gate holds whoever fails the MCQ. That is a switch
 *     that silently rejects people.
 *   - select is a single answer and would grade correctly, so its exclusion is
 *     the server's call rather than a property of the data. Allowing it means
 *     changing _validate_correct_option, not this constant.
 */
const KEYED_TYPES = ['radio'];

/**
 * The options textarea is one option per line.
 *
 * Deliberately identical to what handleSubmit did before the answer key
 * existed — no trimming — so the dropdown offers exactly the strings that get
 * saved as options, and this change alters no existing question.
 */
const parseOptions = (text) => (text || '').split('\n').filter(Boolean);

/**
 * The two non-position values the ?position= filter can take; anything else is
 * a position id. Strings, because a <select> and a query string both hand back
 * strings, and "all" is simply the absence of the param.
 */
const FILTER_ALL = 'all';
const FILTER_COMMON = 'common';

export default function FormBuilder() {
  const { departmentId } = useParams();
  const { canModule } = useAuth();
  const canAdd = canModule('hr_form_builder', 'add');
  const [questions, setQuestions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editQ, setEditQ] = useState(null);
  const [form, setForm] = useState({
    question_text: '', question_type: 'text', options: '',
    placeholder: '', required: true, order_num: 0, position_id: '',
    correct_option: '',
  });

  /**
   * The filter lives in the query string rather than in useState for two
   * reasons: a refresh or a pasted link keeps it, and it survives a remount.
   * App.jsx's FeatureRoute renders null while AuthContext is loading, which
   * unmounts this page and would drop a local filter on the floor — the same
   * trap as the CvScreening reload path.
   */
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    formsAPI.getQuestions(departmentId).then((res) => setQuestions(res.data));
    // Positions power the scope selector ('' = common). Failure is non-fatal:
    // the selector just shows "All positions (common)" only.
    cvAPI.listPositions(departmentId)
      .then((res) => setPositions(res.data || []))
      .catch(() => setPositions([]));
  }, [departmentId]);

  // scope value in the form is a string ('' = common); convert for the API.
  const scopeToApi = (v) => (v === '' || v == null ? null : parseInt(v, 10));
  const positionTitle = (pid) => positions.find((p) => p.id === pid)?.title;

  // ── The filter, read back out of the URL ───────────────────────────────

  const knownPositionIds = useMemo(
    () => new Set(positions.map((p) => p.id)), [positions]);

  /**
   * An unknown, malformed or not-yet-loaded id falls back to "All" silently
   * and WITHOUT rewriting the URL. Not rewriting is the point: `positions` is
   * empty on the first render and again whenever its fetch fails, and a
   * rewrite would burn a shared ?position=16 link on a transient error. Left
   * alone, the filter simply reappears once the fetch lands.
   */
  const rawFilter = searchParams.get('position');
  const filter = useMemo(() => {
    if (rawFilter === FILTER_COMMON) return FILTER_COMMON;
    if (!rawFilter) return FILTER_ALL;
    const id = Number(rawFilter);
    return Number.isInteger(id) && knownPositionIds.has(id) ? id : FILTER_ALL;
  }, [rawFilter, knownPositionIds]);

  const onPosition = typeof filter === 'number';
  // Only meaningful inside a position's form; ignored for All and Common.
  const hideCommon = onPosition && searchParams.get('hide_common') === '1';
  const filtering = filter !== FILTER_ALL;

  // Both writers use the functional form so they cannot clobber each other or
  // any unrelated param, and replace: true so changing the filter does not
  // fill the back button with a trail of dead ends.
  const selectFilter = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === FILTER_ALL) next.delete('position');
      else next.set('position', value);
      // "Hide common" has no meaning outside a position, and left set it would
      // be a filter still in force that the panel no longer shows.
      if (!/^\d+$/.test(value)) next.delete('hide_common');
      return next;
    }, { replace: true });
  };

  const selectHideCommon = (on) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (on) next.set('hide_common', '1');
      else next.delete('hide_common');
      return next;
    }, { replace: true });
  };

  // ── The lists, all derived from `questions` ────────────────────────────

  /**
   * The full list in its real order. order_num is GLOBAL across the
   * department — routers/forms.py::questions_for_position merges the common
   * and position-scoped questions and orders them by order_num alone — so
   * this one sort stays correct under every filter.
   */
  const sortedQuestions = useMemo(
    () => [...questions].sort((a, b) => a.order_num - b.order_num), [questions]);

  /**
   * Q number = place in the FULL list, so a filtered view reads Q3, Q7, Q9
   * rather than renumbering to 1, 2, 3 and implying order is per position.
   */
  const globalIndex = useMemo(() => {
    const m = new Map();
    sortedQuestions.forEach((q, i) => m.set(q.id, i + 1));
    return m;
  }, [sortedQuestions]);

  /**
   * Derived, never stored. edit/delete/toggle-active all write to `questions`
   * and this recomputes from it, so acting on a filtered row updates the real
   * list and leaves the filter exactly where it was.
   *
   * A position shows what a candidate for it would see: its own questions plus
   * the common ones — the same union routers/forms.py::questions_for_position
   * builds, minus its active-only filter, because HR still has to reach the
   * hidden ones to edit them.
   */
  const visibleQuestions = useMemo(() => {
    if (filter === FILTER_ALL) return sortedQuestions;
    if (filter === FILTER_COMMON) return sortedQuestions.filter((q) => q.position_id == null);
    return sortedQuestions.filter(
      (q) => q.position_id === filter || (!hideCommon && q.position_id == null));
  }, [sortedQuestions, filter, hideCommon]);

  const commonCount = useMemo(
    () => questions.filter((q) => q.position_id == null).length, [questions]);

  const countByPosition = useMemo(() => {
    const m = new Map();
    questions.forEach((q) => {
      if (q.position_id != null) m.set(q.position_id, (m.get(q.position_id) || 0) + 1);
    });
    return m;
  }, [questions]);

  /**
   * Positions worth offering: the ones that actually own a question, inactive
   * ones included — an inactive position's questions still exist and still
   * need editing, so leaving it out would strand the only way to reach them.
   *
   * The selected position is kept even at zero, so deleting a position's last
   * question lands on its empty state rather than silently throwing you back
   * to All while you are still working on it.
   */
  const filterablePositions = useMemo(
    () => positions.filter((p) => countByPosition.has(p.id) || p.id === filter),
    [positions, countByPosition, filter]);

  const openCreate = () => {
    setEditQ(null);
    setForm({
      question_text: '', question_type: 'text', options: '', placeholder: '',
      required: true, order_num: questions.length,
      // Pre-select the position being filtered on, still editable in the form.
      // order_num stays the global count: the sequence is department-wide.
      position_id: onPosition ? String(filter) : '',
      correct_option: '',
    });
    setShowModal(true);
  };

  const openEdit = (q) => {
    setEditQ(q);
    setForm({
      question_text: q.question_text, question_type: q.question_type,
      options: q.options ? q.options.join('\n') : '',
      placeholder: q.placeholder || '', required: q.required, order_num: q.order_num,
      position_id: q.position_id == null ? '' : String(q.position_id),
      correct_option: q.correct_option || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasOptions = OPTION_TYPES.includes(form.question_type);
    const opts = parseOptions(form.options);
    // Send a key ONLY for the types whose answer is one option. Grading in
    // services/form_screening_service.py is type-agnostic — it grades every
    // question whose correct_option is not null — so a key left behind on a
    // question switched to Short Text would silently start grading it. Null
    // here is what un-grades it.
    const keyed = KEYED_TYPES.includes(form.question_type) && opts.includes(form.correct_option);
    const payload = {
      ...form,
      options: hasOptions && form.options ? opts : null,
      correct_option: keyed ? form.correct_option : null,
      position_id: scopeToApi(form.position_id),
    };
    delete payload.options_text;
    try {
      if (editQ) {
        const res = await formsAPI.updateQuestion(editQ.id, payload);
        setQuestions((prev) => prev.map((q) => (q.id === editQ.id ? res.data : q)));
        toast.success('Question updated');
      } else {
        const res = await formsAPI.createQuestion(departmentId, payload);
        setQuestions((prev) => [...prev, res.data]);
        toast.success('Question added');
      }
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const deleteQ = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    await formsAPI.deleteQuestion(id);
    setQuestions((prev) => prev.filter((q) => q.id !== id));
    toast.success('Question deleted');
  };

  const toggleActive = async (q) => {
    const res = await formsAPI.updateQuestion(q.id, { active: !q.active });
    setQuestions((prev) => prev.map((item) => (item.id === q.id ? res.data : item)));
  };

  const hasOptions = OPTION_TYPES.includes(form.question_type);
  const optionList = parseOptions(form.options);
  const showCorrect = KEYED_TYPES.includes(form.question_type);

  // Shared by the header button and the filtered empty state, so the two can
  // never drift into looking like different actions.
  const addButtonClass = 'flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Candidate Form Builder</h1>
          <p className="text-sm text-gray-500">
            Customize the form sent to passing candidates
            {filtering && (
              <span className="text-gray-400">
                {' · '}Showing {visibleQuestions.length} of {questions.length} questions
              </span>
            )}
          </p>
        </div>
        <button onClick={openCreate}
          disabled={!canAdd}
          title={canAdd ? '' : "🔒 You don't have permission"}
          className={addButtonClass}>
          <PlusIcon className="w-4 h-4" /> Add Question
        </button>
      </div>

      {questions.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <label htmlFor="position-filter" className="text-sm font-medium text-gray-700">
            Position
          </label>
          {/* Picking a position shows that position's form as its candidates
              see it: its own questions plus the common ones. */}
          <select id="position-filter" value={onPosition ? String(filter) : filter}
            onChange={(e) => selectFilter(e.target.value)}
            className="flex-1 min-w-[15rem] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
            <option value={FILTER_ALL}>All questions ({questions.length})</option>
            <option value={FILTER_COMMON}>Common only — no position ({commonCount})</option>
            {filterablePositions.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {/* "(inactive)" is part of the option text, not a group label:
                    a closed <select> shows only the selected option's own
                    text, so anything else is invisible once picked. */}
                {p.title}{p.active ? '' : ' (inactive)'} ({countByPosition.get(p.id) || 0})
              </option>
            ))}
          </select>
          {onPosition && (
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={hideCommon}
                onChange={(e) => selectHideCommon(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded" />
              Hide common questions
            </label>
          )}
        </div>
      )}

      {filtering && visibleQuestions.length > 0 && (
        <p className="text-xs text-gray-400 mb-3">
          Order is global across the department, so the Q numbers below are each
          question's place in the full list — not in this view.
        </p>
      )}

      {questions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200 text-gray-400">
          <p className="text-4xl mb-3">📋</p>
          <p className="font-medium">No questions yet</p>
          <p className="text-sm mt-1">Add questions to your candidate intake form</p>
        </div>
      ) : visibleQuestions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200 text-gray-400">
          <p className="text-4xl mb-3">📋</p>
          <p className="font-medium text-gray-500">
            {filter === FILTER_COMMON
              ? 'No common questions yet'
              : `No questions for ${positionTitle(filter) || `position #${filter}`} yet`}
          </p>
          <p className="text-sm mt-1">
            {hideCommon
              ? 'Common questions are hidden — untick "Hide common questions" to see them.'
              : 'Add one here, or switch to "All questions" to see the rest.'}
          </p>
          <button onClick={openCreate}
            disabled={!canAdd}
            title={canAdd ? '' : "🔒 You don't have permission"}
            className={`${addButtonClass} mx-auto mt-4`}>
            <PlusIcon className="w-4 h-4" /> Add Question
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleQuestions.map((q) => (
            <div key={q.id} className={`bg-white rounded-xl border p-4 ${q.active ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {/* Decorative. Nothing here drags — order_num is edited one
                      question at a time in the modal. If drag is ever added it
                      MUST be disabled while `filtering` is true: order_num is
                      one global sequence, so writing a filtered subset's order
                      would reshuffle the questions this view is hiding. */}
                  <span className="text-gray-300 flex-shrink-0">
                    <Bars3Icon className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 font-mono">Q{globalIndex.get(q.id)}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                        {QUESTION_TYPES.find((t) => t.value === q.question_type)?.label}
                      </span>
                      {/* Scope badge — why this question appears, at a glance */}
                      {q.position_id == null ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-100">
                          Common · all positions
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-100">
                          {positionTitle(q.position_id) || `Position #${q.position_id}`}
                        </span>
                      )}
                      {q.required && <span className="text-red-500 text-xs">Required</span>}
                    </div>
                    <p className="font-medium text-gray-800 mt-0.5">{q.question_text}</p>
                    {q.options?.length > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5">Options: {q.options.join(', ')}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => toggleActive(q)}
                    className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                      q.active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}>
                    {q.active ? 'Active' : 'Hidden'}
                  </button>
                  <button onClick={() => openEdit(q)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors">
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteQ(q.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)}
        title={editQ ? 'Edit Question' : 'Add Question'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Question Text *</label>
            <input required value={form.question_text}
              onChange={(e) => setForm({ ...form, question_text: e.target.value })}
              placeholder="e.g. What is your expected salary?"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Show this question for</label>
            <select value={form.position_id}
              onChange={(e) => setForm({ ...form, position_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
              <option value="">All positions (common)</option>
              {positions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Common questions appear on every position's form. A specific position shows this only to that position's candidates.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
              <select value={form.question_type}
                onChange={(e) => setForm({
                  ...form,
                  question_type: e.target.value,
                  // Drop the key the moment the type can no longer carry one,
                  // so the field disappearing and the value going away are the
                  // same event rather than two the user has to connect.
                  correct_option: KEYED_TYPES.includes(e.target.value) ? form.correct_option : '',
                })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
              <input type="number" value={form.order_num}
                onChange={(e) => setForm({ ...form, order_num: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder Text</label>
            <input value={form.placeholder}
              onChange={(e) => setForm({ ...form, placeholder: e.target.value })}
              placeholder="Optional hint text"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
          {hasOptions && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Options <span className="text-xs text-gray-400">(one per line)</span>
              </label>
              <textarea value={form.options}
                onChange={(e) => {
                  // Editing the options can orphan the chosen answer. Keep it
                  // only while it is still one of them, so the dropdown can
                  // never show a key that is no longer on offer.
                  const next = parseOptions(e.target.value);
                  setForm({
                    ...form,
                    options: e.target.value,
                    correct_option: next.includes(form.correct_option) ? form.correct_option : '',
                  });
                }}
                rows={4}
                placeholder="Option 1&#10;Option 2&#10;Option 3"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 font-mono" />
            </div>
          )}
          {showCorrect && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correct Answer <span className="text-xs text-gray-400">(optional)</span>
              </label>
              {optionList.length === 0 ? (
                <p className="text-xs text-gray-400">Add options above to choose a correct answer.</p>
              ) : (
                <>
                  <select value={form.correct_option}
                    onChange={(e) => setForm({ ...form, correct_option: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                    <option value="">No correct answer — not graded</option>
                    {optionList.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Candidates never see this. Setting it makes the question count towards the
                    applicant's score, and a position's pass mark is judged on the questions that have one.
                  </p>
                </>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input type="checkbox" id="required" checked={form.required}
              onChange={(e) => setForm({ ...form, required: e.target.checked })}
              className="w-4 h-4 text-primary-600 rounded" />
            <label htmlFor="required" className="text-sm font-medium text-gray-700">Required field</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowModal(false)}
              className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit"
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">
              {editQ ? 'Save' : 'Add Question'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
